import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import clsx from 'clsx';
import React, { Component } from 'react';
import { withTranslation } from 'react-i18next';
import SyntaxHighlighter, { createElement } from 'react-syntax-highlighter';
import { UncontrolledTooltip } from 'reactstrap';
import NumericSummary from './NumericSummary';
import Issue from './Issue';
import { ISSUES_GROUP } from '../../../constants/api';
import ClearFilterReport from '../components/ClearFilterReport';
import { IS_APP_CODEAC } from '../../../constants/env';
import { withPlanInfo } from '../../../components/hoc/WithPlanInfo';
import TooManyIssuesMessage from './TooManyIssuesMessage';
import FileIcon from './FileIcon';
import { withQuickFixSuggestionsModal } from './QuickFixesSuggestionsModal';
import Icon from '../../../components/Icon';
export const knownExtensions = {
    java: 'java',
    jbuilder: 'ruby',
    js: 'javascript',
    php: 'php',
    rb: 'ruby',
    ru: 'ruby',
    ruby: 'ruby',
    sass: 'scss',
    scss: 'scss',
    yaml: 'yaml',
    yml: 'yaml',
};
const knownFilenames = {
    gemfile: 'ruby',
    rakefile: 'ruby',
};
const CHARS_PER_LINES = 105;
const SPACING_BETWEEN_LINES = 0.8 * 16;
const PADDING_LINES_COUNT = 10;
// Workaround for syntax highlighter https://github.com/conorhastings/react-syntax-highlighter/issues/200
const Pre = ({ realClassName, realStyle, children }) => {
    return (_jsx("pre", { className: realClassName, style: realStyle, children: children }));
};
class Report extends Component {
    constructor() {
        super(...arguments);
        this.state = {
            expandedIssues: {},
        };
        this.toggleIssueDocs = (blockIndex, nodeIndex, messageIndex) => {
            const { expandedIssues } = this.state, nodeId = `${blockIndex}#${nodeIndex}`, exists = Object.prototype.hasOwnProperty.call(expandedIssues, nodeId) &&
                // @ts-expect-error TS7053: Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{}'.
                Array.isArray(expandedIssues[nodeId]) &&
                // @ts-expect-error TS7053: Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{}'.
                expandedIssues[nodeId].includes(messageIndex);
            if (exists) {
                this.setState({
                    expandedIssues: {
                        ...expandedIssues,
                        // @ts-expect-error TS7053: Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{}'.
                        [nodeId]: expandedIssues[nodeId].filter((index) => index !== messageIndex),
                    },
                });
            }
            else {
                this.setState({
                    expandedIssues: {
                        ...expandedIssues,
                        [nodeId]: Object.prototype.hasOwnProperty.call(expandedIssues, nodeId)
                            // @ts-expect-error TS7053: Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{}'.
                            ? [...expandedIssues[nodeId], messageIndex]
                            : [messageIndex],
                    },
                });
            }
        };
        this.getExpandedClasses = (index, i) => {
            const hasIndexedProperty = Object.prototype.hasOwnProperty.call(this.state.expandedIssues, `${index}#${i}`);
            // @ts-expect-error TS7053: Element implicitly has an 'any' type because expression of type '`${number}#${number}`' can't be used to index type '{}'.
            const isExpanded = hasIndexedProperty && this.state.expandedIssues[`${index}#${i}`].length;
            return clsx('report-detail__code-issues-container', {
                'report-detail__code-issues-container--expanded': isExpanded,
            });
        };
        this.handleClickOnFilePath = (filePath) => {
            navigator.clipboard.writeText(filePath);
            this.setState({ copiedFilePath: true });
            setTimeout(() => {
                this.setState({ copiedFilePath: false });
            }, 5000);
        };
    }
    renderSyntaxHighlighter(block, index, language, blocks, sourceLines) {
        const { coveredLines, file, t } = this.props;
        const lineNumberStyle = (lineNumber) => {
            const issues = block.messages[lineNumber - 1];
            if (issues && issues.length > 0) {
                // @ts-expect-error TS2345: Argument of type string is not assignable to parameter of type Record<string, any>[]
                const linesCount = this.countLines(issues);
                const issuesSpacing = (issues.length - 1) * SPACING_BETWEEN_LINES;
                return {
                    display: 'block',
                    marginBottom: 21 * linesCount + 25 + issuesSpacing,
                };
            }
            return {};
        };
        const lineProps = (lineNumber) => {
            const issues = block.messages[block.startLine + lineNumber - 1];
            const lineCoverage = coveredLines?.[block.startLine + lineNumber];
            const props = {
                id: file.filePath + ':' + (block.startLine + lineNumber),
                ...(typeof lineCoverage !== 'undefined' ? {
                    className: lineCoverage > 0 ? 'covered' : 'not-covered',
                    title: t('coveredLine', { count: lineCoverage }),
                } : {}),
            };
            const messagesCount = Object.values(block.messages).filter((message) => message && message.length > 0).length;
            if (issues && issues.length > 0) {
                // @ts-expect-error TS2345: Argument of type string is not assignable to parameter of type Record<string, any>[]
                const linesCount = this.countLines(issues);
                const issuesSpacing = (issues.length - 1) * SPACING_BETWEEN_LINES;
                return {
                    ...props,
                    className: `highlighted${props.className ? ' ' + props.className : ''}`,
                    style: {
                        marginBottom: messagesCount === 1 && block.startLine === 0 && block.startLine === block.endLine
                            ? 0
                            : 21 * linesCount + 25 + issuesSpacing,
                        display: 'block',
                    },
                };
            }
            return props;
        };
        const renderer = ({ rows, stylesheet, useInlineStyles }) => {
            // we want to display also errors in empty files, so we need to iterate over something on the lines bellow this assignment
            if (rows.length === 0 && file.messages.length > 0) {
                rows = [
                    {
                        type: 'element',
                        tagName: 'span',
                        properties: {
                            id: file.filePath + ':1',
                            className: [],
                        },
                        children: [],
                    },
                ];
            }
            return rows.map((node, i) => (_jsxs("div", { className: "report-detail__code-line", children: [createElement({
                        node: node,
                        stylesheet,
                        useInlineStyles,
                        key: `code-segement${i}`,
                    }), !block.messages[block.startLine + i] || block.messages[block.startLine + i].length === 0 ? null : (_jsx("div", { className: "report-detail__code-issues", children: _jsx("div", { className: `${this.getExpandedClasses(index, i)}`, children: block.messages[block.startLine + i].map((message, key) => (_jsx(Issue, { issue: message, className: "report-detail__code-issue issue", onIssueExpandClick: () => this.toggleIssueDocs(index, i, key) }, key))) }) }))] }, i)));
        };
        const realStyle = () => {
            let style = {}, blockIndex, nodeIndex;
            for (const blockId of Object.keys(this.state.expandedIssues)) {
                [blockIndex, nodeIndex] = blockId.split('#');
                blockIndex = parseInt(blockIndex);
                nodeIndex = parseInt(nodeIndex);
                if (blockIndex === index &&
                    // @ts-expect-error TS2769: No overload matches this call.
                    this.state.expandedIssues[blockId].length &&
                    nodeIndex + 1 + blocks[blockIndex].startLine > blocks[blockIndex].endLine - 10) {
                    style = {
                        paddingBottom: Math.max(3, 15 - blocks[blockIndex].endLine + (nodeIndex + 1 + blocks[blockIndex].startLine)) + 'rem',
                    };
                }
            }
            return style;
        };
        return (_jsx(SyntaxHighlighter, { language: language, useInlineStyles: false, showLineNumbers: true, startingLineNumber: block.startLine + 1, wrapLines: true, lineNumberStyle: lineNumberStyle, lineProps: lineProps, renderer: renderer, lineNumberContainerStyle: {
                borderRight: '0.0625rem solid #999',
                color: '#999',
                marginRight: '1rem',
                paddingLeft: '1rem',
                paddingRight: '10px',
                textAlign: 'right',
                float: 'left',
            }, PreTag: Pre, realClassName: clsx('report-detail__code', {
                'report-detail__code--ellipsis-top': block.startLine > 0,
                // @ts-expect-error TS2339: Property 'length' does not exist on type 'Record<number, Block>'.
                'report-detail__code--ellipsis-bottom': index === blocks.length - 1 && block.endLine < sourceLines.length,
            }), realStyle: realStyle(), children: Array.from({ length: block.endLine - block.startLine }, (value, key) => key + block.startLine)
                .map((lineNumber) => sourceLines[lineNumber])
                .join('\n') }, index));
    }
    calculateRemainingLines() {
        const { file } = this.props;
        const remainingLines = {};
        file.messages.forEach((message) => {
            const startLine = message.line - PADDING_LINES_COUNT >= 0 ? message.line - PADDING_LINES_COUNT : 0;
            const endLine = (message.endLine ? message.endLine : message.line) + PADDING_LINES_COUNT;
            for (let i = startLine; i < endLine; i++) {
                if (typeof remainingLines[i] === 'undefined') {
                    remainingLines[i] = [];
                }
            }
            if (typeof remainingLines[message.line - 1] !== 'undefined') {
                remainingLines[message.line - 1].push(message);
            }
        });
        return remainingLines;
    }
    countLines(issues) {
        return issues
            .map((issue) => {
            return Math.ceil((issue.message.length + (issue.ruleId ? issue.ruleId.length : 0) + 3) / CHARS_PER_LINES);
        })
            .reduce((acc, val) => acc + val, 0);
    }
    filterRowsByHiddenType(lines) {
        const { hiddenType } = this.props;
        if (!lines.length) {
            return;
        }
        return lines.filter((line) => hiddenType === ISSUES_GROUP.ERRORS ? line.isError === false : line.isError === true);
    }
    renderClearFilterReport(showTrialContent) {
        const { file, generated, user } = this.props;
        return (_jsx("div", { className: "trial-report", children: _jsx(ClearFilterReport, { showTrial: showTrialContent, file: file, generated: generated, errorsLeft: file.errorCount, warningsLeft: file.warningCount, user: user }) }));
    }
    render() {
        const { file, generated, hiddenType, isRepositoryPrivate, proPlan, t, onLoadClick, fileIndex, showQuickFixesClick, } = this.props;
        // @ts-expect-error TS2339: Property 'copiedFilePath' does not exist on type '{ expandedIssues: {}; }'.
        const { copiedFilePath } = this.state;
        const remainLines = this.calculateRemainingLines();
        const sourceLines = file.source.split(/\r\n|\n|\r/);
        const totalLinesCount = sourceLines.length;
        const blocks = [];
        let blockIndex = 0;
        for (let i = 0; i < totalLinesCount; i++) {
            if (typeof remainLines[i] === 'undefined') {
                if (blockIndex + 1 === blocks.length) {
                    blockIndex++;
                }
            }
            else {
                if (blockIndex + 1 > blocks.length) {
                    blocks.push({
                        startLine: i,
                        endLine: Math.min(i + PADDING_LINES_COUNT, totalLinesCount),
                        messages: { [i]: hiddenType ? this.filterRowsByHiddenType(remainLines[i]) : remainLines[i] },
                    });
                }
                else {
                    blocks[blockIndex].endLine =
                        remainLines[i].length > 0 ? Math.min(i + PADDING_LINES_COUNT, totalLinesCount) : i;
                    blocks[blockIndex].messages[i] = hiddenType
                        ? this.filterRowsByHiddenType(remainLines[i])
                        : remainLines[i];
                }
            }
        }
        let language = void 0, matches = (file.filePath || '').match(/\.([a-z]+)$/);
        if (matches && Object.prototype.hasOwnProperty.call(knownExtensions, matches[1])) {
            // @ts-expect-error TS7053: Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ gemfile: string; rakefile: string; }'.
            language = knownExtensions[matches[1]];
        }
        else {
            matches = (file.filePath || '').toLocaleLowerCase().match(/(?:.*\/)?(.+)$/);
            if (matches && Object.prototype.hasOwnProperty.call(knownFilenames, matches[1])) {
                // @ts-expect-error TS7053: Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ gemfile: string; rakefile: string; }'.
                language = knownFilenames[matches[1]];
            }
        }
        const showTrialContent = IS_APP_CODEAC && !proPlan && fileIndex !== 1 && isRepositoryPrivate;
        const showTruncated = file.truncated && file.showTruncated && blocks.length > 0;
        return showTrialContent ? (this.renderClearFilterReport(showTrialContent)) : (_jsxs("div", { className: clsx('report-detail__item', generated && 'report-detail__item--generated'), id: file.filePath, children: [_jsx("span", { className: "float-right report-detail__item-issue-count", children: _jsx(NumericSummary, { errorCount: file.errorCount, warningCount: file.warningCount, hiddenType: hiddenType }) }), _jsxs("h3", { className: "report-detail__file-path", children: [_jsx(FileIcon, { file: file, onLoadClick: onLoadClick, blocksLength: blocks.length }), _jsxs("bdi", { id: `report-path${fileIndex}`, onClick: () => this.handleClickOnFilePath(file.filePath), children: [file.filePath, _jsx("span", { className: "cursor--pointer", onClick: () => showQuickFixesClick(file.fixes), children: file.fixes && (_jsx(Icon, { name: "bolt", title: t('quickFixesHelp'), className: "mx-1 text-warning" })) }), !generated ? null : _jsxs("small", { className: "ml-1", children: ["(", t('generated').toLocaleLowerCase(), ")"] })] })] }), blocks.map((block, index) => 
                // @ts-expect-error TS2345: Argument of type undefined is not assignable to parameter of type string
                this.renderSyntaxHighlighter(block, index, language, blocks, sourceLines)), showTruncated ? (_jsx(TooManyIssuesMessage, { file: file, onLoadClick: onLoadClick, standalone: false })) : null, blocks.length === 0 ? _jsx(TooManyIssuesMessage, { file: file, onLoadClick: onLoadClick }) : null, _jsx(UncontrolledTooltip, { target: `report-path${fileIndex}`, children: copiedFilePath ? t('copiedFilePath') : file.filePath })] }));
    }
}
Report.defaultProps = {
    generated: false,
    copiedFilePath: false,
};
export default withQuickFixSuggestionsModal(withPlanInfo(withTranslation()(Report)));
